import api from '../../services/api';

import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Form, Input, Button, Select, InputNumber, Typography, Spin, Alert, Checkbox } from 'antd';
import { debounce } from 'lodash';
import { Liability, Asset, PostLiability } from '../../types';
import { AssetService } from '../assets/asset.service';

const { Title } = Typography;
const { Option } = Select;

interface AddEditLiabilityFormProps {
  visible: boolean;
  onClose: () => void;
  initialData?: Liability;
  onSubmit: (data: Liability) => void;
  estateId: string;
}

const AddEditLiabilityForm: React.FC<AddEditLiabilityFormProps> = ({
  visible,
  onClose,
  initialData,
  onSubmit,
  estateId,
}) => {
  const [form] = Form.useForm();
  const [assetOptions, setAssetOptions] = useState<Asset[]>([]);
  const [fetching, setFetching] = useState<boolean>(false);
  const [fetchError, setFetchError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const isEditing = !!initialData;
  const [creditors, setCreditors] = useState<Array<{ uid: string; name: string }>>([]);
  const [creditorName, setCreditorName] = useState('');
  const [creditorAddress, setCreditorAddress] = useState('');
  useEffect(() => {
    if (visible && estateId) api.get(`/liabilities/${estateId}/creditors/catalogue`)
      .then(r => setCreditors(r.data)).catch(() => setFetchError('Could not load creditors'));
  }, [visible, estateId]);
  const addCreditor = async () => {
    try {
      const response = await api.post(`/liabilities/${estateId}/creditors/catalogue`, { name: creditorName, address: creditorAddress });
      setCreditors(old => [...old, response.data]);
      form.setFieldValue('creditorUid', response.data.uid);
      setCreditorName(''); setCreditorAddress('');
    } catch { setFetchError('Could not save creditor'); }
  };


  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        ...initialData,
        assetId: typeof initialData.asset === 'object' ? (initialData.asset as any)?.uid : initialData.asset,
        ...Object.fromEntries(['dateOfDeathBalance','outstandingBalance','securedAmount','collateralValue'].map(key => [key, (initialData as any)[key] == null ? undefined : Number((initialData as any)[key])])),
        description: initialData.description,
        monthlyPayment: parseFloat(initialData.amount),
        source: initialData.source,
        name: initialData.name,
      });
    } else {
      form.resetFields();
    }
  }, [initialData, form]);

  // Debounced function to fetch assets
  const fetchAssetOptions = useCallback(
    debounce(async (value: string) => {
      if (!value) {
        setAssetOptions([]);
        setFetching(false);
        return;
      }
      setFetching(true);
      setFetchError('');
      try {
        const assets = await AssetService.searchAssets(estateId, value);
        setAssetOptions(assets);
      } catch (error: any) {
        console.error('Error fetching assets:', error);
        setFetchError(error.message || 'Failed to fetch assets.');
      } finally {
        setFetching(false);
      }
    }, 500), // 500ms debounce
    [estateId],
  );

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    fetchAssetOptions(value);
  };

  const handleSelect = (value: number) => {
    // Additional logic on select, if needed
  };

  const handleFinish = (values: any) => {
    const liability: Liability = {
      ...values,
      dateIncurred: values.dateIncurred || null,
      closedDate: values.closedDate || null,
      uid: initialData?.uid || '',
      estateId,
      asset: values.assetId || null,
      amount: values.monthlyPayment,
        };
    onSubmit(liability);
    // Optionally reset fields or close modal
    // form.resetFields();
    // onClose();
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      title={isEditing ? 'Edit Liability' : 'Add Liability'}
      destroyOnClose
      width={600}
      bodyStyle={{ padding: '24px' }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        // initialValues={initialData || { monthlyPayment: 0 }}
      >
        <Title level={5}>Liability Details</Title>

        <Form.Item
          label="Name"
          name="name"
          rules={
            isEditing
              ? [] // No validation rules in edit mode
              : [{ required: true, message: 'Enter a name for the liability' }]
          }
        >
          <Input placeholder="Name of the liability" />
        </Form.Item>

        {fetchError && <Alert message={fetchError} type="error" showIcon style={{ marginBottom: '16px' }} />}

        <Form.Item
          label="Secured asset (optional)"
          name="assetId"
          rules={
            isEditing
              ? []
              : []
          }
        >
          <Select
            showSearch
            placeholder="Search and select an asset"
            notFoundContent={fetching ? <Spin size="small" /> : null}
            filterOption={false} // Disable built-in filtering to use server-side search
            onSearch={handleSearch}
            onSelect={handleSelect}
            allowClear
            loading={fetching}
          >
            {assetOptions.map((asset) => (
              <Option key={asset.uid} value={asset.uid}>
                {asset.name} - {asset.description}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          rules={
            isEditing
              ? []
              : [{ required: true, message: 'Please enter a description' }]
          }
        >
          <Input.TextArea rows={3} placeholder="Enter liability description" />
        </Form.Item>

        <Form.Item
          label="Monthly Payment"
          name="monthlyPayment"
          rules={
            isEditing
              ? [
                  { type: 'number', min: Number.NEGATIVE_INFINITY, message: 'Monthly Payment must be a number' },
                ]
              : [
                  { required: true, message: 'Please enter the monthly payment' },
                  { type: 'number', min: Number.NEGATIVE_INFINITY, message: 'Monthly Payment must be a number' },
                ]
          }
        >
          <InputNumber style={{ width: '100%' }} placeholder="e.g., 500" />
        </Form.Item>

        <Form.Item
          label="Source"
          name="source"
          rules={
            isEditing
              ? []
              : [{ required: true, message: 'Please enter the source' }]
          }
        >
          <Input placeholder="Source of liability" />
        </Form.Item>

        <Form.Item label="Creditor" name="creditorUid"><Select allowClear showSearch optionFilterProp="label" options={creditors.map(c => ({ value: c.uid, label: c.name }))} /></Form.Item>
        <Input placeholder="New creditor name" value={creditorName} onChange={e => setCreditorName(e.target.value)} maxLength={255} />
        <Input.TextArea placeholder="Creditor address" value={creditorAddress} onChange={e => setCreditorAddress(e.target.value)} maxLength={4000} />
        <Button disabled={!creditorName.trim()} onClick={addCreditor} style={{ marginBottom: 16 }}>Add to creditor catalogue</Button>
        <Form.Item label="Status" name="status" initialValue="open"><Select options={[{ value: 'open', label: 'Open' }, { value: 'closed', label: 'Closed / fully paid' }]} /></Form.Item>
        <Form.Item label="Claim category" name="claimCategory"><Select options={['funeral','medical','debt','mortgage_lien','administration','other'].map(value => ({ value, label: value.replace('_', ' ') }))} /></Form.Item>
        <Form.Item name="estateTaxDeductible" valuePropName="checked"><Checkbox>Deductible on the federal estate-tax return</Checkbox></Form.Item>
        <Form.Item label="ET-706 deduction location" name="deductionScope"><Select allowClear options={[
          {value:'ny_property', label:'Directly related to New York property'},
          {value:'outside_ny_property', label:'Directly related to property outside New York'},
          {value:'intangible', label:'Directly related to intangible property'},
          {value:'unallocated', label:'Not directly related to particular property'},
        ]} /></Form.Item>
        <Form.Item label="Who owes the debt?" name="debtor"><Select options={[
          {value: 'decedent', label: 'Decedent (debtor 1)'}, {value: 'spouse', label: 'Spouse (debtor 2)'},
          {value: 'joint', label: 'Both jointly'}, {value: 'other_or_uncertain', label: 'At least one debtor or another / uncertain'},
        ]} /></Form.Item>
        <Form.Item label="Treatment on death (as documented)" name="deathTreatment"><Select options={['estate','surviving_spouse','company','forgiven','other','unconfirmed'].map(value => ({ value, label: value.replace('_', ' ') }))} /></Form.Item>
        {['collateralDescription','lienNature','accountReference'].map(name => <Form.Item key={name} name={name} label={{ collateralDescription: 'Collateral description', lienNature: 'Nature of lien', accountReference: 'Account reference' }[name]}><Input maxLength={255} /></Form.Item>)}
        {['dateOfDeathBalance','outstandingBalance','securedAmount','collateralValue'].map(name => <Form.Item key={name} name={name} label={{dateOfDeathBalance: 'Balance at date of death', outstandingBalance: 'Outstanding claim balance', securedAmount: 'Secured portion of claim', collateralValue: 'Collateral value'}[name]}><InputNumber min={0} precision={2} style={{width: '100%'}} /></Form.Item>)}
        <Form.Item label="Date incurred" name="dateIncurred"><Input type="date" /></Form.Item>
        <Form.Item label="Closure date" name="closedDate"><Input type="date" /></Form.Item>


        <Form.Item>
          <Button type="primary" htmlType="submit" style={{ marginRight: '8px' }}>
            {isEditing ? 'Update Liability' : 'Add Liability'}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddEditLiabilityForm;
